import { useState, useCallback, useEffect } from 'react'
import { Command } from '../config/commands'

const RECENT_COMMANDS_KEY = 'fmeastudio-recent-commands'
const MAX_RECENT_COMMANDS = 5

/**
 * Hook for managing command palette state
 *
 * Handles:
 * - Open/close state
 * - Search query
 * - Selected index for keyboard navigation
 * - Recent commands tracking
 */
export function useCommandPalette() {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentCommands, setRecentCommands] = useState<string[]>(() => {
    // Load recent commands from localStorage
    try {
      const stored = localStorage.getItem(RECENT_COMMANDS_KEY)
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })

  // Open command palette
  const open = useCallback(() => {
    setIsOpen(true)
    setSearchQuery('')
    setSelectedIndex(0)
  }, [])

  // Close command palette
  const close = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
    setSelectedIndex(0)
  }, [])

  // Toggle command palette
  const toggle = useCallback(() => {
    if (isOpen) {
      close()
    } else {
      open()
    }
  }, [isOpen, open, close])

  // Update search query
  const updateSearchQuery = useCallback((query: string) => {
    setSearchQuery(query)
    setSelectedIndex(0) // Reset selection when query changes
  }, [])

  // Navigate selection with arrow keys
  const selectNext = useCallback((maxIndex: number) => {
    setSelectedIndex((prev) => (prev + 1) % maxIndex)
  }, [])

  const selectPrevious = useCallback((maxIndex: number) => {
    setSelectedIndex((prev) => (prev - 1 + maxIndex) % maxIndex)
  }, [])

  const setSelected = useCallback((index: number) => {
    setSelectedIndex(index)
  }, [])

  // Execute command and track it
  const executeCommand = useCallback(
    (command: Command) => {
      // Execute the command
      command.execute()

      // Add to recent commands
      const updated = [
        command.id,
        ...recentCommands.filter((id) => id !== command.id),
      ].slice(0, MAX_RECENT_COMMANDS)

      setRecentCommands(updated)

      // Persist to localStorage
      try {
        localStorage.setItem(RECENT_COMMANDS_KEY, JSON.stringify(updated))
      } catch {
        // Ignore localStorage errors
      }

      // Close palette
      close()
    },
    [recentCommands, close]
  )

  // Reset selection when opening
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0)
    }
  }, [isOpen])

  return {
    isOpen,
    searchQuery,
    selectedIndex,
    recentCommands,
    open,
    close,
    toggle,
    updateSearchQuery,
    selectNext,
    selectPrevious,
    setSelected,
    executeCommand,
  }
}
